import axios from 'axios';
import {
  BULK_ASSIGN_COMPLIANCE_QUESTIONS_URL,
  BULK_UNASSIGN_COMPLIANCE_QUESTIONS_URL,
  TOGGLE_TRANSITION_RULE_DOCUMENT_URL,
  get_request_config
} from '@/utils/system_constants/constant';
import { build_assignment_key } from './document_helpers';

export default {
  persist_document_rule_assignment(compliance_rule_id, section_question_id, should_assign) {
    const url = should_assign
      ? BULK_ASSIGN_COMPLIANCE_QUESTIONS_URL
      : BULK_UNASSIGN_COMPLIANCE_QUESTIONS_URL;

    return axios.post(
      url,
      {
        compliance_rule_id: Number(compliance_rule_id),
        section_question_ids: [section_question_id]
      },
      get_request_config()
    );
  },
  get_document_assigned_compliance_rules(document) {
    const section_question_id = Number(document?.section_question_id || 0);
    if (!section_question_id) {
      return [];
    }

    return this.all_rule_documents.filter((assigned_document) =>
      Number(assigned_document.section_question_id) === section_question_id
    );
  },
  get_document_selected_compliance_rule_id(document) {
    return String(this.get_document_assigned_compliance_rules(document)?.[0]?.rule_id || '');
  },
  get_document_assigned_transition_rules(document) {
    const section_question_id = String(document?.section_question_id || '');
    if (!section_question_id) {
      return [];
    }

    return this.transition_rules.filter((transition_rule) =>
      this.get_transition_rule_document_ids(transition_rule).includes(section_question_id)
    );
  },
  get_document_selected_transition_rule_id(document) {
    return String(this.get_document_assigned_transition_rules(document)?.[0]?.id || '');
  },
  get_document_compliance_rule_label(document) {
    const assignments = this.get_document_assigned_compliance_rules(document);
    if (!assignments.length) return 'No rule assigned';
    return assignments.map((assignment) => assignment.rule_name).join(', ');
  },
  get_document_transition_rule_label(document) {
    const assignments = this.get_document_assigned_transition_rules(document);
    if (!assignments.length) return 'No rule assigned';
    return assignments.map((assignment) => assignment.name).join(', ');
  },
  is_document_compliance_configured(document) {
    return this.get_document_assigned_compliance_rules(document).length > 0;
  },
  is_document_transition_configured(document) {
    return this.get_document_assigned_transition_rules(document).length > 0;
  },
  async assign_compliance_rule_to_document(document, next_rule_id) {
    const section_question_id = Number(document?.section_question_id || 0);
    if (!section_question_id) {
      return;
    }

    const normalized_next_rule_id = Number(next_rule_id || 0);
    this.updating_document_rule_id = build_assignment_key('compliance', section_question_id);
    await this.save_document_compliance_assignment(document, section_question_id, normalized_next_rule_id)
      .catch((error) => this.report_compliance_rule_error(error))
      .finally(() => this.clear_updating_document_rule());
  },
  async save_document_compliance_assignment(document, section_question_id, normalized_next_rule_id) {
    await this.replace_document_compliance_rule(document, section_question_id, normalized_next_rule_id);
    await this.fetch_documents_for_all_rules();
    this.report_compliance_rule_result(normalized_next_rule_id);
  },
  report_compliance_rule_result(normalized_next_rule_id) {
    this.show_success(normalized_next_rule_id ? 'Compliance rule saved.' : 'Compliance rule removed.');
  },
  report_compliance_rule_error(error) {
    const response_data = this.parse_response_data(error?.response?.data || {});
    this.show_error(response_data.message || 'Failed to save compliance rule.');
  },
  async replace_document_compliance_rule(document, section_question_id, normalized_next_rule_id) {
    await this.remove_previous_compliance_rules(document, section_question_id, normalized_next_rule_id);
    await this.save_next_compliance_rule(normalized_next_rule_id, section_question_id);
  },
  remove_previous_compliance_rules(document, section_question_id, normalized_next_rule_id) {
    const existing_rules = this.get_document_assigned_compliance_rules(document);
    return Promise.all(
      existing_rules
        .filter((rule) => Number(rule.rule_id) !== normalized_next_rule_id)
        .map((rule) => this.persist_document_rule_assignment(rule.rule_id, section_question_id, false))
    );
  },
  save_next_compliance_rule(normalized_next_rule_id, section_question_id) {
    return normalized_next_rule_id
      ? this.persist_document_rule_assignment(normalized_next_rule_id, section_question_id, true)
      : Promise.resolve();
  },
  async assign_transition_rule_to_document(document, next_rule_id) {
    const section_question_id = Number(document?.section_question_id || 0);
    if (!section_question_id) {
      return;
    }

    const normalized_next_rule_id = Number(next_rule_id || 0);
    this.updating_document_rule_id = build_assignment_key('transition', section_question_id);
    await this.save_document_transition_assignment(document, section_question_id, normalized_next_rule_id)
      .catch((error) => this.report_transition_rule_error(error))
      .finally(() => this.clear_updating_document_rule());
  },
  async save_document_transition_assignment(document, section_question_id, normalized_next_rule_id) {
    await this.replace_document_transition_rule(document, section_question_id, normalized_next_rule_id);
    await this.fetch_transition_rules_for_selected_form();
    this.report_transition_rule_result(normalized_next_rule_id);
  },
  clear_updating_document_rule() {
    this.updating_document_rule_id = '';
  },
  report_transition_rule_result(normalized_next_rule_id) {
    this.show_success(normalized_next_rule_id ? 'Transition rule saved.' : 'Transition rule removed.');
  },
  report_transition_rule_error(error) {
    const response_data = this.parse_response_data(error?.response?.data || {});
    this.show_error(response_data.message || 'Failed to save transition rule.');
  },
  async replace_document_transition_rule(document, section_question_id, normalized_next_rule_id) {
    await this.remove_previous_transition_rules(document, section_question_id, normalized_next_rule_id);
    await this.save_next_transition_rule(normalized_next_rule_id, section_question_id);
  },
  remove_previous_transition_rules(document, section_question_id, normalized_next_rule_id) {
    const existing_rules = this.get_document_assigned_transition_rules(document);
    return Promise.all(
      existing_rules
        .filter((rule) => Number(rule.id) !== normalized_next_rule_id)
        .map((rule) => this.persist_transition_rule_assignment(rule.id, section_question_id, false))
    );
  },
  save_next_transition_rule(normalized_next_rule_id, section_question_id) {
    return normalized_next_rule_id
      ? this.persist_transition_rule_assignment(normalized_next_rule_id, section_question_id, true)
      : Promise.resolve();
  },
  async assign_selected_documents_to_compliance_rule() {
    const rule_id = Number(this.bulk_selected_rule_id || 0);
    const section_question_ids = this.selected_document_ids.map((id) => Number(id)).filter((id) => id > 0);

    if (!rule_id) {
      this.show_error('Please select a compliance rule.');
      return;
    }
    if (!section_question_ids.length) {
      this.show_error('Please select at least one document field.');
      return;
    }

    this.assigning_rule = true;
    try {
      await Promise.all(section_question_ids.map((section_question_id) => {
        const document = this.documents.find((item) => Number(item.section_question_id) === section_question_id);
        return this.assign_compliance_rule_to_document(document, rule_id);
      }));
      await this.fetch_documents_for_all_rules();
      this.show_success('Compliance rule assigned to selected document fields.');
    } finally {
      this.assigning_rule = false;
    }
  },
  persist_transition_rule_assignment(transition_rule_id, section_question_id, is_assigned) {
    return axios.post(
      TOGGLE_TRANSITION_RULE_DOCUMENT_URL,
      {
        transition_rule_id: Number(transition_rule_id),
        section_question_id: Number(section_question_id),
        is_assigned: Boolean(is_assigned)
      },
      get_request_config()
    );
  },
  async assign_selected_documents_to_transition_rule() {
    const rule_id = Number(this.bulk_selected_transition_rule_id || 0);
    const section_question_ids = this.selected_document_ids.map((id) => Number(id)).filter((id) => id > 0);

    if (!rule_id) {
      this.show_error('Please select a transition rule.');
      return;
    }
    if (!section_question_ids.length) {
      this.show_error('Please select at least one document field.');
      return;
    }

    this.assigning_transition_rule = true;
    try {
      await Promise.all(section_question_ids.map((section_question_id) => {
        const document = this.documents.find((item) => Number(item.section_question_id) === section_question_id);
        return this.assign_transition_rule_to_document(document, rule_id);
      }));
      await this.fetch_transition_rules_for_selected_form();
      this.show_success('Transition rule assigned to selected document fields.');
    } finally {
      this.assigning_transition_rule = false;
    }
  }
};
